<template>
  <div>
    <div class="flex items-center justify-between mb-2">
      <div class="flex items-center gap-2">
        <span class="text-md font-bold">Impact Statement</span>
        <div v-if="hasImpactStatements" class="relative">
          <TooltipProvider :delayDuration="0">
            <Tooltip>
              <TooltipTrigger as-child>
                <button
                  @click="copyAllImpactStatements"
                  type="button"
                  class="flex items-center justify-center p-1 hover:bg-gray-200 rounded-full transition-colors"
                  aria-label="Copy all impact statements"
                >
                  <span class="material-icons-outlined text-xl">content_copy</span>
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" v-if="!isTooltipOpen">
                <p>Copy impact statements</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider :delayDuration="0">
            <Tooltip :open="isTooltipOpen">
              <TooltipTrigger as-child>
                <div class="absolute inset-0 pointer-events-none"></div>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>Statements copied!</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    </div>

    <!-- Impact review results -->
    <ContentReviewResults
      v-if="isReviewing || reviewError || (reviewResults && reviewResults.length > 0)"
      title="Impact Statement Review"
      class="mb-4"
      :results="reviewResults || []"
      :is-loading="isReviewing || false"
      :error="reviewError || null"
      :expected-count="expectedReviewCount || 0"
      loading-text="Reviewing Impact Statement..."
      @toggle="(result, expanded) => $emit('toggleReview', result, expanded)"
      @close="$emit('closeReview')"
    />

    <!-- Render each impact statement -->
    <div v-if="hasImpactStatements" class="flex flex-col gap-4 mb-4">
      <div 
        v-for="(statement, index) in impactStatements" 
        :key="index"
        class="flex flex-col gap-1"
      >
        <div class="flex items-center justify-between">
          <div class="text-sm text-black max-w-[90%]">
            <span class="font-bold">{{ statement.designation_label }}</span>: {{ statement.impact_area_labels.join(' | ') }}
          </div>
          <TooltipProvider :delayDuration="0">
            <Tooltip>
              <TooltipTrigger as-child>
                <button
                  type="button"
                  @click="$emit('remove', index)"
                  class="text-blue-400 hover:text-blue-500 text-sm font-normal transition-colors"
                >
                  Clear
                </button>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p>Clear impact statement</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <WikiTextEditor
          :ref="(el) => setEditorRef(index, el)"
          :wikiText="getEditableText(index)"
          @update:wikiText="(val: string) => setEditableText(index, val)"
          :toEmbeddedText="toEmbeddedText"
          :toWikiText="toWikiText"
          :outer-class="''"
          :placeHolder="'Enter impact statement...'"
          :reset-value="statement.statement_text"
          edited-text-type="impact_statement"
          :hide-controls="true"
          @editModeUpdated="(isEditMode: boolean) => handleEditModeUpdated(index, isEditMode)"
        />
      </div>
    </div>

    <!-- Dropdown and Add Button - always visible at bottom -->
    <div class="flex flex-col gap-2">
      <div class="flex items-end gap-2 py-1">
        <Dropdown
          v-model="selectedDesignation"
          :options="designationOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Select a disability designation"
          aria-label="Disability designation"
          class="w-[300px] !border !border-gray-600 !rounded-[4px]"
          inputClass="!h-8 !leading-8 !py-0 text-md"
          panelClass="text-md"
          :class="{ 'border-red-500': showError }"
        />
        <GbButton
          @click="handleAddStatement"
          gbTheme="bluePrimary"
          class="!px-4 !py-2 text-md"
        >
          <span class="material-icons-round text-lg mr-1">add_circle</span>
          Add Statement
        </GbButton>
      </div>
      <div v-if="showError" class="flex items-center gap-1 text-sm text-red-300">
        <span class="material-icons-round text-base">warning</span>
        <span>Select a designation above</span>
      </div>
    </div>

    <PresentlevelsImpactStatementModal
      :visible="showModal"
      :subject-label="displaySubjectLabel || subjectLabel"
      :student-name="studentName"
      :disability-uuid="selectedDesignation"
      :disability-label="selectedDisabilityLabel"
      :impact-areas="impactAreasForSelectedDisability"
      @close="showModal = false"
      @insert="handleInsertStatement"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import Dropdown from 'primevue/dropdown'
import GbButton from '@/components/GbButton.vue'
import WikiTextEditor from '@/components/editor/WikiTextEditor.vue'
import PresentlevelsImpactStatementModal from './PresentlevelsImpactStatementModal.vue'
import { getDisabilitiesAndImpactAreas } from '@/impact-of-disability/api'
import { useUserStore } from '@/authentication/user.store'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import { transformResourceComponentsToWiki, transformWikiToResourceComponents } from '@/components/editor/wiki_tiptap_helpers'
import { getEditorPlainText } from './copy-statement-helpers'
import { useCopyText } from '@/util/use_copy_text'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/tooltip'
import type { DisabilityOption, ImpactArea } from '@/impact-of-disability/impact-of-disability_types'
import type { ImpactStatement } from './presentlevels_types'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import ContentReviewResults from '@/content-review/components/ContentReviewResults.vue'
import type { AuditResult } from '@/content-review/api'

const props = defineProps<{
  subjectLabel: string
  displaySubjectLabel?: string
  studentName: string
  impactStatements?: ImpactStatement[]
  reviewResults?: AuditResult[]
  isReviewing?: boolean
  reviewError?: string | null
  expectedReviewCount?: number
}>()

const emit = defineEmits<{
  add: [statement: ImpactStatement]
  remove: [index: number]
  update: [index: number, statementText: string]
  closeReview: []
  toggleReview: [result: AuditResult, expanded: boolean]
}>()

const toEmbeddedText = transformWikiToResourceComponents
const toWikiText = transformResourceComponentsToWiki

const userStore = useUserStore()
const contentProfileStore = useContentProfileStore()
const { copyText, isTooltipOpen, showTooltipUntilDismissed } = useCopyText()
const wikiTextEditorRefs = ref<Map<number, InstanceType<typeof WikiTextEditor>>>(new Map())
const analytics = getAnalyticsTracker()

function setEditorRef(index: number, el: any) {
  if (el) {
    wikiTextEditorRefs.value.set(index, el)
  }
}

const selectedDesignation = ref<string>('')
const showModal = ref(false)
const showError = ref(false)
const loadingOptions = ref(false)
const designationOptions = ref<DisabilityOption[]>([])
const impactAreasByDisability = ref<Map<string, ImpactArea[]>>(new Map())
const editableStatements = ref<Map<number, string>>(new Map())

// Initialize editable statements from props
watch(() => props.impactStatements, (newStatements) => {
  if (newStatements) {
    // Clear old entries that no longer exist
    const validIndices = new Set(newStatements.map((_, i) => i))
    Array.from(editableStatements.value.keys()).forEach(key => {
      if (!validIndices.has(key)) {
        editableStatements.value.delete(key)
      }
    })
    
    // Update all statements (not just new ones) to handle discard/reset
    newStatements.forEach((statement, index) => {
      editableStatements.value.set(index, statement.statement_text)
    })
  }
}, { immediate: true, deep: true })

const hasImpactStatements = computed(() => {
  return props.impactStatements && props.impactStatements.length > 0
})

function handleEditModeUpdated(index: number, isEditMode: boolean) {
  const editableText = editableStatements.value.get(index)
  const originalText = props.impactStatements?.[index]?.statement_text
  if (!isEditMode && editableText !== originalText) {
    emit('update', index, editableText || '')
  }
}

function getEditableText(index: number): string {
  const text = editableStatements.value.get(index)
  if (text !== undefined) return text
  return props.impactStatements?.[index]?.statement_text || ''
}

function setEditableText(index: number, text: string) {
  editableStatements.value.set(index, text)
}

const selectedDisabilityLabel = computed(() => {
  const option = designationOptions.value.find(opt => opt.value === selectedDesignation.value)
  return option?.label || ''
})

const impactAreasForSelectedDisability = computed(() => {
  if (!selectedDesignation.value) return []
  return impactAreasByDisability.value.get(selectedDesignation.value) || []
})

onMounted(async () => {
  await fetchDisabilitiesAndImpactAreas()
})

async function fetchDisabilitiesAndImpactAreas() {
  loadingOptions.value = true
  
  try {
    const response = await getDisabilitiesAndImpactAreas(
      userStore.licenseSubType,
      contentProfileStore.profileId
    )
    
    if (response.disabilities) {
      designationOptions.value = response.disabilities.map(item => ({
        label: item.disability,
        value: item.disability_uuid,
        locked: false
      }))
      
      impactAreasByDisability.value = new Map(
        response.disabilities.map(item => [item.disability_uuid, item.areas_of_impact])
      )
    }
  } catch (error) {
    console.error('Failed to load disabilities and impact areas:', error)
  } finally {
    loadingOptions.value = false
  }
}

function handleAddStatement() {
  if (!selectedDesignation.value) {
    showError.value = true
    return
  }
  analytics.sendEvent(EVENT_NAMES.PRESENTLEVELS.SUMMARY.ADDED_IMPACT_STATEMENT, {
    iod_selected_disability: selectedDisabilityLabel.value
  })
  showError.value = false
  showModal.value = true
}

function handleInsertStatement(impactAreaUuids: string[], statementText: string) {
  // Get the impact area labels from the UUIDs
  const areaLabels = impactAreaUuids
    .map(uuid => impactAreasForSelectedDisability.value.find(area => area.area_uuid === uuid)?.area_label)
    .filter((label): label is string => !!label)
  
  const newStatement: ImpactStatement = {
    designation_uuid: selectedDesignation.value,
    designation_label: selectedDisabilityLabel.value,
    impact_area_uuids: impactAreaUuids,
    impact_area_labels: areaLabels,
    statement_text: statementText
  }
  

  emit('add', newStatement)
  showModal.value = false
  selectedDesignation.value = ''
}

async function copyAllImpactStatements(event: MouseEvent) {
  const formattedStatements: string[] = []
  
  // Build the header
  const header = `Impact Statement in ${props.subjectLabel}`
  
  // Get all impact statements from the editors with their metadata
  props.impactStatements?.forEach((statement, index) => {
    const editorRef = wikiTextEditorRefs.value.get(index)
    if (editorRef) {
      const processedText = getEditorPlainText(editorRef)
      if (processedText.trim()) {
        // Build disability header for this statement
        const disabilityHeader = `${statement.designation_label}: ${statement.impact_area_labels.join(' | ')}`
        
        // Split statement into paragraphs and join with double line breaks
        const formattedText = processedText.split('\n').filter(line => line.trim()).join('\n\n')
        
        // Combine disability header + empty line + statement text
        formattedStatements.push(`${disabilityHeader}\n\n${formattedText}`)
      }
    }
  })
  
  if (formattedStatements.length > 0) {
    // Combine: header + empty line + all statements (each with their own disability header)
    const combinedText = `${header}\n\n${formattedStatements.join('\n\n')}`
    const triggerElement = event.currentTarget as HTMLElement

    try {
      await copyText(combinedText, event, { autoHideTooltip: true, autoHideDelay: 3000 })
    } catch (error) {
      console.error('Failed to copy impact statements:', error)
      showTooltipUntilDismissed(triggerElement, 3000)
    }
  }
}
</script>

<style scoped>
.p-dropdown.border-red-500 {
  border-color: #ef4444;
}
</style>
